import { BackgroundWork } from "./utils/background_work.js";
import { CameraController } from "./core/camera_controller.js";
import { Renderer } from "./core/renderer.js";
import { GraphicsSettings, effectiveUiPixelRatio } from "./graphics/graphics_quality.js";
import { SceneManager } from "./core/scene_manager.js";
import { FontLoader } from "./fonts/font_loader.js";
import { LocalizationManager } from "./localization/localization_manager.js";
import { GlobalSettings } from "./global_settings.js";
import { fillAllImagesInfo } from "./image_loader/all_images_info.js";
import { ImgID } from "./image_loader/img_id.js";
import { ImagesHolder } from "./image_loader/images_holder.js";
import { NineSliceDrawer } from "./image_loader/nine_slicer/nine_slice_drawer.js";
import { LayoutScalerLocal } from "./layout_scaler/layout_scaler.js";
import { BackButtonHandler } from "./navigation/back_button_handler.js";
import { PhysicsWorld } from "./physics/physics_world.js";
import { fillAllResourcesInfo } from "./resource_loader/all_resources_info.js";
import { ResourceManager } from "./resource_loader/resource_manager.js";
import { ScreenManager, Screens } from "./screen_manager/screen_manager.js";
import { DOM_UI_KEYS, canvasHasContext, markCanvasHasContext, resolveUiBackend } from "./ui/dom_backend/dom_backend.js";
import { createNullCanvasContext } from "./ui/dom_backend/null_canvas_context.js";
import { StatisticsStorage } from "./storage/statistics_storage.js";
import { TelegramAPISettings } from "./telegram_api_settings.js";
import { DialogManager } from "./ui/dialog/dialog_manager.js";
import { UIScrollableContainer } from "./ui/common_ui_elements/ui_scrollable_container.js";
import { OverlayManager } from "./ui/overlay/overlay_manager.js";
import { CoinFlightHud } from "./ui/overlay/coin_flight_hud.js";
import { NavigationBlock } from "./ui/navigation_block/navigation_block.js";
import { LeaderboardScreen } from "./ui/screens/leaderboard/leaderboard_screen.js";
import { LeaderboardBoardService, LeaderboardCountryBoardService } from "./api/leaderboard/leaderboard_board_service.js";
import { GameScreen } from "./ui/screens/game_screen/game_screen.js";
import { GameUI } from "./ui/screens/game_screen/ui/game_ui.js";
import { PrizeShotUI } from "./ui/screens/prize_shot_screen/prize_shot_ui.js";
import { PrizeShotScreen } from "./prize_shot/prize_shot_screen.js";
import { WebGLUIRuntime } from "./webgl_2d/webgl_ui_runtime.js";
import { Vfx2DRuntime } from "./webgl_2d/vfx_2d_runtime.js";
import { Vfx2DGlobalAtlas } from "./webgl_2d/core/rendering/textures/vfx_2d_global_atlas.js";
import { VFX_2D_ATLAS_CONFIG, VFX_2D_ATLAS_SIZE } from "./webgl_2d/vfx_2d_atlas_config.js";
import { LoadingScreen } from "./ui/screens/loading_screen/loading_screen.js";
import { LoadingGameScreen, LoadingGameVariant } from "./ui/screens/loading_game_screen.ts/loading_game_screen.js";
import { MenuScreen } from "./ui/screens/menu_screen/menu_screen.js";
import { SettingsDialog } from "./ui/dialog/settings_dialog.js";
import { AvatarSelectionDialog } from "./ui/dialog/avatar_selection_dialog.js";
import { LeaveLevelDialog } from "./ui/dialog/leave_level_dialog.js";
import { MoreLivesDialog } from "./ui/dialog/more_lives_dialog.js";
import { PlayDialog } from "./ui/dialog/play_dialog.js";
import { ProfileDialog } from "./ui/dialog/profile_dialog.js";
import { ShopScreen } from "./ui/screens/shop_screen/shop_screen.js";
import { FpsOverlay } from "./debug/fps_overlay.js";
import { UiPerfProbe } from "./debug/ui_perf_probe.js";
import { PerfSession } from "./debug/perf_session.js";
import { PhysicsDebugRenderer } from "./debug/physics_debug_renderer.js";
import { AudioController, audioController } from "./audio/audio_controller.js";
import { FeedbackScreen } from "./ui/screens/feedback_screen/feedback_screen.js";
import { isMobileDevice } from "./utils/device_utils.js";
import { SimulationContext } from "./simulation/simulation_context.js";
import { RateGameDialog } from "./ui/dialog/rate_game_dialog.js";
import { TestLevelBridge } from "./debug/test_level_bridge.js";
import { StartupImagePlan } from "./image_loader/startup_image_plan.js";
import { StartupProgress } from "./ui/screens/loading_screen/startup_progress.js";
import { runStabilityBatch } from "./debug/stability_check.js";
import { runSimulationBatch, runSimulationCollector } from "./debug/simulation_batch.js";
import { UpdateRequiredDialog, UpdateRequiredMode } from "./ui/dialog/update_required_dialog.js";
import { OverlayId } from "./ui/overlay/overlay_ids.js";
import { OverlayFactory } from "./ui/overlay/overlay_factory.js";
import { GameResultOverlayParams } from "./ui/overlay/overlays/game_result_overlay.js";
import { getDailyChallengeRewards } from "./daily_challenge/daily_challenge_rewards.js";
import { DAILY_CHALLENGE_DAYS_COUNT } from "./daily_challenge/daily_challenge_config.js";
import { Vector2 } from "./common/vector2.js";
import { TriangleBench } from "./debug/triangle_bench.js";
import { NetworkManager } from "./network_manager.js";
import { isNetworkishError } from "./utils/network_errors.js";
import { DailyTasksScreen } from "./ui/screens/daily_tasks_screen/daily_tasks_screen.js";
import { AdventureFloorInfo, AdventureScreen } from "./ui/screens/adventure_screen/adventure_screen.js";
import type { WinStreakTowerDto } from "./api/dto/win_streak_tower.dto.js";
import { TooltipManager } from "./ui/tooltip/tooltip_manager.js";
import { AvatarUtils } from "./common/avatar_utils.js";
import { DailyChallengeScreen } from "./ui/screens/daily_challenge_screen/daily_challenge_screen.js";
import { DailyChallengeCompletedDialog } from "./ui/dialog/daily_challenge_completed_dialog.js";
import { ChainOfferScreen } from "./ui/screens/chain_offer_screen/chain_offer_screen.js";
import { OnePlusOneOfferScreen, FREE_BUTTON_HUE_ROTATION } from "./ui/screens/one_plus_one_offer_screen/one_plus_one_offer_screen.js";
import { UIImage } from "./ui/common_ui_elements/ui_image.js";
import { warmCanvas2DShaders } from "./ui/common_ui_elements/render_utils.js";
import type { IOnePlusOneOfferParams } from "./ui/screens/one_plus_one_offer_screen/one_plus_one_offer_screen.js";
import { ONE_PLUS_ONE_PLACEMENT, ONE_PLUS_TWO_PLACEMENT, isOnePlusOneOffer, getOnePlusOneState } from "./offers/one_plus_one_offer.js";
import { OfferService, getPlatformProductConfig } from "./offers/offer_service.js";
import { IapPlugin } from "./plugins/iap_plugin.js";
import { OnePlusTwoOfferScreen } from "./ui/screens/one_plus_two_offer_screen/one_plus_two_offer_screen.js";
import type { IOnePlusTwoOfferParams } from "./ui/screens/one_plus_two_offer_screen/one_plus_two_offer_screen.js";
import { StartOfferScreen } from "./ui/screens/start_offer_screen/start_offer_screen.js";
import { ChainVideoScreen } from "./ui/screens/chain_video_screen/chain_video_screen.js";
import { crashCapture } from "./crash/crash_reporter.js";
import { isMemoryConstrained } from "./utils/memory_policy.js";
import { TowerFailDialog } from "./ui/dialog/tower_fail_dialog.js";

export class Game
{
  public static instance: Game;

  private static readonly Y_OVERFLOW = 1; // hack for Telegram issue

  public static max_physics_steps = 3;
  public static physics_budget_ms = 8;
  public static physics_adaptive = true;
  public static physics_slow_mode = false;
  public static physics_slow_enter_ms = 8;
  public static physics_slow_exit_ms = 4;

  private vfx_main_ui_back_canvas: HTMLCanvasElement;
  private game_canvas: HTMLCanvasElement;
  private adventure_canvas: HTMLCanvasElement;
  private daily_challenge_canvas: HTMLCanvasElement;
  private chain_offer_canvas: HTMLCanvasElement;
  private one_plus_one_offer_canvas: HTMLCanvasElement;
  private one_plus_two_offer_canvas: HTMLCanvasElement;
  private start_offer_canvas: HTMLCanvasElement;
  private chain_video_canvas: HTMLCanvasElement;
  private tooltip_canvas: HTMLCanvasElement;
  private menu_canvas: HTMLCanvasElement;
  private shop_canvas: HTMLCanvasElement;
  private leaderboard_canvas: HTMLCanvasElement;
  private feedback_canvas: HTMLCanvasElement;
  private webgl_ui_main_canvas: HTMLCanvasElement;
  private vfx_main_ui_front_canvas: HTMLCanvasElement;
  private navigation_canvas: HTMLCanvasElement;
  private loading_canvas: HTMLCanvasElement;
  private dialog_canvas: HTMLCanvasElement;
  private vfx_overlay_back_canvas: HTMLCanvasElement;
  private vfx_overlay_front_canvas: HTMLCanvasElement;
  private overlay_canvas: HTMLCanvasElement;

  private game_ctx: CanvasRenderingContext2D;
  private adventure_ctx: CanvasRenderingContext2D;
  private daily_challenge_ctx: CanvasRenderingContext2D;
  private chain_offer_ctx: CanvasRenderingContext2D;
  private one_plus_one_offer_ctx: CanvasRenderingContext2D;
  private one_plus_two_offer_ctx: CanvasRenderingContext2D;
  private start_offer_ctx: CanvasRenderingContext2D;
  private chain_video_ctx: CanvasRenderingContext2D;
  private tooltip_ctx: CanvasRenderingContext2D;
  private menu_ctx: CanvasRenderingContext2D;
  private shop_ctx: CanvasRenderingContext2D;
  private leaderboard_ctx: CanvasRenderingContext2D;
  private feedback_ctx: CanvasRenderingContext2D;
  private navigation_ctx: CanvasRenderingContext2D;
  private loading_ctx!: CanvasRenderingContext2D;
  private dialog_ctx: CanvasRenderingContext2D;
  private overlay_ctx: CanvasRenderingContext2D;

  /** HUD of the GAME screen: a level, or a Prize Shot round. */
  private game_ui?: GameUI | PrizeShotUI;
  private adventure_screen?: AdventureScreen;
  private daily_challenge_screen?: DailyChallengeScreen;
  private chain_offer_screen?: ChainOfferScreen;
  private one_plus_one_offer_screen?: OnePlusOneOfferScreen;
  private one_plus_two_offer_screen?: OnePlusTwoOfferScreen;
  private start_offer_screen?: StartOfferScreen;
  private chain_video_screen?: ChainVideoScreen;
  private menu_screen?: MenuScreen;
  private shop_screen?: ShopScreen;
  private leaderboard_screen?: LeaderboardScreen;
  private daily_tasks_screen?: DailyTasksScreen;
  private feedback_screen?: FeedbackScreen;
  private navigation_block?: NavigationBlock;
  private loading_screen?: LoadingScreen;
  private startup_loader_as_game_loading = false;
  private loading_game_screen?: LoadingGameScreen;
  private loading_game_variant: LoadingGameVariant = 'default';
  private loading_game_screen_shown_at = 0;
  private static readonly MAX_SCENE_REVEAL_HOLD_MS = 2500;
  private scene_reveal_hold_until = 0;
  private scene_revealed = true;
  private menu_startup_ready: Promise<void> = Promise.resolve();

  private static SCREEN_WIDTH: number;
  private static SCREEN_HEIGHT: number;

  private static SAFE_AREA_TOP: number = 0;
  private static SAFE_AREA_BOTTOM: number = 0;
  private static SAFE_AREA_LEFT: number = 0;
  private static SAFE_AREA_RIGHT: number = 0;

  private static HORIZONTAL_W_TO_H_RATIO: number = 0.75;

  private game_x = 0;

  private static is_mobile: boolean;

  private static dots_per_pixel: number;

  /** Delays only the first menu render until its server-backed event state is ready. */
  public setMenuStartupReadyPromise( promise: Promise<void> ): void
  {
    this.menu_startup_ready = promise;
  }

  constructor(
    screen_width: number = window.innerWidth,
    screen_height: number = window.innerHeight,
  )
  {
    Game.instance = this;
    Game.is_mobile = isMobileDevice();

    const horizontal_screen: boolean = screen_width / screen_height > Game.HORIZONTAL_W_TO_H_RATIO;
    this.game_x = horizontal_screen ? Math.round( ( screen_width - screen_height * Game.HORIZONTAL_W_TO_H_RATIO ) / 2 ) : 0;

    if ( horizontal_screen )
    {
      screen_width = Math.round( screen_height * Game.HORIZONTAL_W_TO_H_RATIO );
    }

    Game.dots_per_pixel = effectiveUiPixelRatio( GraphicsSettings.profile() );
    const dpr = Game.dots_per_pixel;

    window.scrollTo( 0, 0 );

    const safe_area_insets = TelegramAPISettings.getSafeAreaInsets();
    Game.SAFE_AREA_TOP = safe_area_insets.top;
    Game.SAFE_AREA_BOTTOM = safe_area_insets.bottom;
    Game.SAFE_AREA_LEFT = safe_area_insets.left;
    Game.SAFE_AREA_RIGHT = safe_area_insets.right;

    const logical_screen_w = screen_width;
    const logical_screen_h = screen_height;

    const screen_w = Math.round( dpr * screen_width );
    const screen_h = Math.round( dpr * screen_height );

    Game.SCREEN_WIDTH = logical_screen_w;
    Game.SCREEN_HEIGHT = logical_screen_h;

    this.vfx_main_ui_back_canvas = document.getElementById( "vfxMainUIBackCanvas" ) as HTMLCanvasElement;
    this.game_canvas = document.getElementById( "gameUICanvas" ) as HTMLCanvasElement;
    this.adventure_canvas = document.getElementById( "adventureCanvas" ) as HTMLCanvasElement;
    this.daily_challenge_canvas = document.getElementById( "dailyChallengeCanvas" ) as HTMLCanvasElement;
    this.chain_offer_canvas = document.getElementById( "chainOfferCanvas" ) as HTMLCanvasElement;
    this.one_plus_one_offer_canvas = document.getElementById( "onePlusOneOfferCanvas" ) as HTMLCanvasElement;
    this.one_plus_two_offer_canvas = document.getElementById( "onePlusTwoOfferCanvas" ) as HTMLCanvasElement;
    this.start_offer_canvas = document.getElementById( "startOfferCanvas" ) as HTMLCanvasElement;
    this.chain_video_canvas = document.getElementById( "chainVideoCanvas" ) as HTMLCanvasElement;
    this.tooltip_canvas = document.getElementById( "tooltipCanvas" ) as HTMLCanvasElement;
    this.menu_canvas = document.getElementById( "menuCanvas" ) as HTMLCanvasElement;
    this.navigation_canvas = document.getElementById( "navigationCanvas" ) as HTMLCanvasElement;
    this.shop_canvas = document.getElementById( "shopCanvas" ) as HTMLCanvasElement;
    this.leaderboard_canvas = document.getElementById( "leaderboardCanvas" ) as HTMLCanvasElement;
    this.feedback_canvas = document.getElementById( "feedbackCanvas" ) as HTMLCanvasElement;
    this.webgl_ui_main_canvas = document.getElementById( "webglUIMainCanvas" ) as HTMLCanvasElement;
    this.vfx_main_ui_front_canvas = document.getElementById( "vfxMainUIFrontCanvas" ) as HTMLCanvasElement;
    this.loading_canvas = document.getElementById( "loadingCanvas" ) as HTMLCanvasElement;
    this.dialog_canvas = document.getElementById( "dialogCanvas" ) as HTMLCanvasElement;
    this.vfx_overlay_back_canvas = document.getElementById( "vfxOverlayBackCanvas" ) as HTMLCanvasElement;
    this.vfx_overlay_front_canvas = document.getElementById( "vfxOverlayFrontCanvas" ) as HTMLCanvasElement;
    this.overlay_canvas = document.getElementById( "overlayCanvas" ) as HTMLCanvasElement;

    const menu_screen_canvases = [
      this.menu_canvas,
    ];
    ScreenManager.registerMenuScreenCanvases( menu_screen_canvases, true );

    const game_screen_canvases = [
      this.game_canvas,
    ];
    ScreenManager.registerGameScreenCanvases( game_screen_canvases, false );

    ScreenManager.registerCanvas( Screens.ADVENTURE, this.adventure_canvas, false );
    ScreenManager.registerCanvas( Screens.DAILY_CHALLENGE, this.daily_challenge_canvas, false );
    ScreenManager.registerCanvas( Screens.CHAIN_OFFER, this.chain_offer_canvas, false );
    ScreenManager.registerCanvas( Screens.ONE_PLUS_ONE_OFFER, this.one_plus_one_offer_canvas, false );
    ScreenManager.registerCanvas( Screens.ONE_PLUS_TWO_OFFER, this.one_plus_two_offer_canvas, false );
    ScreenManager.registerCanvas( Screens.START_OFFER, this.start_offer_canvas, false );
    ScreenManager.registerCanvas( Screens.CHAIN_VIDEO, this.chain_video_canvas, false );
    ScreenManager.registerCanvas( Screens.DAILY_TASKS, this.menu_canvas, false );

    ScreenManager.registerCanvas( Screens.SHOP, this.shop_canvas, true );
    ScreenManager.registerCanvas( Screens.LEADERBOARD, this.leaderboard_canvas, true );
    ScreenManager.registerCanvas( Screens.FEEDBACK, this.feedback_canvas, false );

    this.vfx_main_ui_back_canvas.width = screen_w;
    this.vfx_main_ui_back_canvas.height = screen_h;
    this.vfx_main_ui_back_canvas.style.left = `${this.game_x}px`;
    this.vfx_main_ui_back_canvas.style.top = '0px';
    this.vfx_main_ui_back_canvas.style.width = `${logical_screen_w}px`;
    this.vfx_main_ui_back_canvas.style.height = `${logical_screen_h}px`;
    this.vfx_main_ui_back_canvas.style.zIndex = '1';

    this.game_canvas.width = screen_w;
    this.game_canvas.height = screen_h;
    this.game_canvas.style.left = `${this.game_x}px`;
    this.game_canvas.style.top = '0px';
    this.game_canvas.style.width = `${logical_screen_w}px`;
    this.game_canvas.style.height = `${logical_screen_h}px`;
    this.game_canvas.style.zIndex = '2';

    this.adventure_canvas.width = screen_w;
    this.adventure_canvas.height = screen_h;
    this.adventure_canvas.style.left = `${this.game_x}px`;
    this.adventure_canvas.style.top = '0px';
    this.adventure_canvas.style.width = `${logical_screen_w}px`;
    this.adventure_canvas.style.height = `${logical_screen_h}px`;
    this.adventure_canvas.style.zIndex = '1';

    this.daily_challenge_canvas.width = screen_w;
    this.daily_challenge_canvas.height = screen_h;
    this.daily_challenge_canvas.style.left = `${this.game_x}px`;
    this.daily_challenge_canvas.style.top = '0px';
    this.daily_challenge_canvas.style.width = `${logical_screen_w}px`;
    this.daily_challenge_canvas.style.height = `${logical_screen_h}px`;
    this.daily_challenge_canvas.style.zIndex = '1';

    this.chain_offer_canvas.width = screen_w;
    this.chain_offer_canvas.height = screen_h;
    this.chain_offer_canvas.style.left = `${this.game_x}px`;
    this.chain_offer_canvas.style.top = '0px';
    this.chain_offer_canvas.style.width = `${logical_screen_w}px`;
    this.chain_offer_canvas.style.height = `${logical_screen_h}px`;
    this.chain_offer_canvas.style.zIndex = '1';

    this.one_plus_one_offer_canvas.width = screen_w;
    this.one_plus_one_offer_canvas.height = screen_h;
    this.one_plus_one_offer_canvas.style.left = `${this.game_x}px`;
    this.one_plus_one_offer_canvas.style.top = '0px';
    this.one_plus_one_offer_canvas.style.width = `${logical_screen_w}px`;
    this.one_plus_one_offer_canvas.style.height = `${logical_screen_h}px`;
    this.one_plus_one_offer_canvas.style.zIndex = '1';

    this.one_plus_two_offer_canvas.width = screen_w;
    this.one_plus_two_offer_canvas.height = screen_h;
    this.one_plus_two_offer_canvas.style.left = `${this.game_x}px`;
    this.one_plus_two_offer_canvas.style.top = '0px';
    this.one_plus_two_offer_canvas.style.width = `${logical_screen_w}px`;
    this.one_plus_two_offer_canvas.style.height = `${logical_screen_h}px`;
    this.one_plus_two_offer_canvas.style.zIndex = '1';
    this.start_offer_canvas.width = screen_w;
    this.start_offer_canvas.height = screen_h;
    this.start_offer_canvas.style.left = `${this.game_x}px`;
    this.start_offer_canvas.style.top = '0px';
    this.start_offer_canvas.style.width = `${logical_screen_w}px`;
    this.start_offer_canvas.style.height = `${logical_screen_h}px`;
    this.start_offer_canvas.style.zIndex = '1';

    this.chain_video_canvas.width = screen_w;
    this.chain_video_canvas.height = screen_h;
    this.chain_video_canvas.style.left = `${this.game_x}px`;
    this.chain_video_canvas.style.top = '0px';
    this.chain_video_canvas.style.width = `${logical_screen_w}px`;
    this.chain_video_canvas.style.height = `${logical_screen_h}px`;
    this.chain_video_canvas.style.zIndex = '1';

    this.tooltip_canvas.width = screen_w;
    this.tooltip_canvas.height = screen_h;
    this.tooltip_canvas.style.left = `${this.game_x}px`;
    this.tooltip_canvas.style.top = '0px';
    this.tooltip_canvas.style.width = `${logical_screen_w}px`;
    this.tooltip_canvas.style.height = `${logical_screen_h}px`;
    this.tooltip_canvas.style.zIndex = '2';

    this.menu_canvas.width = screen_w;
    this.menu_canvas.height = screen_h;
    this.menu_canvas.style.left = `${this.game_x}px`;
    this.menu_canvas.style.top = '0px';
    this.menu_canvas.style.width = `${logical_screen_w}px`;
    this.menu_canvas.style.height = `${logical_screen_h}px`;
    this.menu_canvas.style.zIndex = '2';

    this.shop_canvas.width = screen_w;
    this.shop_canvas.height = screen_h;
    this.shop_canvas.style.left = `${this.game_x}px`;
    this.shop_canvas.style.top = '0px';
    this.shop_canvas.style.width = `${logical_screen_w}px`;
    this.shop_canvas.style.height = `${logical_screen_h}px`;
    this.shop_canvas.style.zIndex = '2';

    this.leaderboard_canvas.width = screen_w;
    this.leaderboard_canvas.height = screen_h;
    this.leaderboard_canvas.style.left = `${this.game_x}px`;
    this.leaderboard_canvas.style.top = '0px';
    this.leaderboard_canvas.style.width = `${logical_screen_w}px`;
    this.leaderboard_canvas.style.height = `${logical_screen_h}px`;
    this.leaderboard_canvas.style.zIndex = '2';

    this.navigation_canvas.width = screen_w;
    this.navigation_canvas.height = screen_h;
    this.navigation_canvas.style.left = `${this.game_x}px`;
    this.navigation_canvas.style.top = '0px';
    this.navigation_canvas.style.width = `${logical_screen_w}px`;
    this.navigation_canvas.style.height = `${logical_screen_h}px`;
    this.navigation_canvas.style.zIndex = '3';

    this.feedback_canvas.width = screen_w;
    this.feedback_canvas.height = screen_h;
    this.feedback_canvas.style.left = `${this.game_x}px`;
    this.feedback_canvas.style.top = '0px';
    this.feedback_canvas.style.width = `${logical_screen_w}px`;
    this.feedback_canvas.style.height = `${logical_screen_h}px`;

    this.webgl_ui_main_canvas.width = screen_w;
    this.webgl_ui_main_canvas.height = screen_h;
    this.webgl_ui_main_canvas.style.left = `${this.game_x}px`;
    this.webgl_ui_main_canvas.style.top = '0px';
    this.webgl_ui_main_canvas.style.width = `${logical_screen_w}px`;
    this.webgl_ui_main_canvas.style.height = `${logical_screen_h}px`;
    this.webgl_ui_main_canvas.style.zIndex = '3';

    this.vfx_main_ui_front_canvas.width = screen_w;
    this.vfx_main_ui_front_canvas.height = screen_h;
    this.vfx_main_ui_front_canvas.style.left = `${this.game_x}px`;
    this.vfx_main_ui_front_canvas.style.top = '0px';
    this.vfx_main_ui_front_canvas.style.width = `${logical_screen_w}px`;
    this.vfx_main_ui_front_canvas.style.height = `${logical_screen_h}px`;
    this.vfx_main_ui_front_canvas.style.zIndex = '4';

    this.dialog_canvas.width = screen_w;
    this.dialog_canvas.height = screen_h;
    this.dialog_canvas.style.left = `${this.game_x}px`;
    this.dialog_canvas.style.top = '0px';
    this.dialog_canvas.style.width = `${logical_screen_w}px`;
    this.dialog_canvas.style.height = `${logical_screen_h}px`;

    this.vfx_overlay_back_canvas.width = screen_w;
    this.vfx_overlay_back_canvas.height = screen_h;
    this.vfx_overlay_back_canvas.style.left = `${this.game_x}px`;
    this.vfx_overlay_back_canvas.style.top = '0px';
    this.vfx_overlay_back_canvas.style.width = `${logical_screen_w}px`;
    this.vfx_overlay_back_canvas.style.height = `${logical_screen_h}px`;

    this.vfx_overlay_front_canvas.width = screen_w;
    this.vfx_overlay_front_canvas.height = screen_h;
    this.vfx_overlay_front_canvas.style.left = `${this.game_x}px`;
    this.vfx_overlay_front_canvas.style.top = '0px';
    this.vfx_overlay_front_canvas.style.width = `${logical_screen_w}px`;
    this.vfx_overlay_front_canvas.style.height = `${logical_screen_h}px`;

    this.overlay_canvas.width = screen_w;
    this.overlay_canvas.height = screen_h;
    this.overlay_canvas.style.left = `${this.game_x}px`;
    this.overlay_canvas.style.top = '0px';
    this.overlay_canvas.style.width = `${logical_screen_w}px`;
    this.overlay_canvas.style.height = `${logical_screen_h}px`;

    if ( isMemoryConstrained() )
    {
      for ( const canvas of this.releasableVfxCanvases() )
      {
        canvas.width = 1;
        canvas.height = 1;
      }
      this.releaseScreenCanvas( this.chain_video_canvas );
    }

    this.menu_ctx = this.screenContext( this.menu_canvas, Screens.MENU );
    this.game_ctx = this.screenContext( this.game_canvas, Screens.GAME );
    this.navigation_ctx = this.screenContext( this.navigation_canvas, DOM_UI_KEYS.NAVIGATION );
    this.shop_ctx = this.screenContext( this.shop_canvas, Screens.SHOP );
    this.dialog_ctx = this.screenContext( this.dialog_canvas, DOM_UI_KEYS.DIALOG );
    this.leaderboard_ctx = this.screenContext( this.leaderboard_canvas, Screens.LEADERBOARD );
    this.overlay_ctx = this.screenContext( this.overlay_canvas, DOM_UI_KEYS.OVERLAY );
    this.feedback_ctx = this.screenContext( this.feedback_canvas, Screens.FEEDBACK );
    this.adventure_ctx = this.screenContext( this.adventure_canvas, Screens.ADVENTURE );
    this.tooltip_ctx = this.screenContext( this.tooltip_canvas, DOM_UI_KEYS.TOOLTIP );
    this.daily_challenge_ctx = this.screenContext( this.daily_challenge_canvas, Screens.DAILY_CHALLENGE );
    this.chain_offer_ctx = this.screenContext( this.chain_offer_canvas, Screens.CHAIN_OFFER );
    this.one_plus_one_offer_ctx = this.screenContext( this.one_plus_one_offer_canvas, Screens.ONE_PLUS_ONE_OFFER );
    this.one_plus_two_offer_ctx = this.screenContext( this.one_plus_two_offer_canvas, Screens.ONE_PLUS_TWO_OFFER );
    this.chain_video_ctx = this.screenContext( this.chain_video_canvas, Screens.CHAIN_VIDEO );
    this.start_offer_ctx = this.screenContext( this.start_offer_canvas, Screens.START_OFFER );

    if ( dpr !== 1 )
    {
      for ( const ctx of [
        this.menu_ctx, this.game_ctx, this.navigation_ctx, this.shop_ctx, this.dialog_ctx,
        this.leaderboard_ctx, this.overlay_ctx, this.feedback_ctx, this.adventure_ctx, this.tooltip_ctx,
        this.daily_challenge_ctx, this.chain_offer_ctx, this.one_plus_one_offer_ctx, this.one_plus_two_offer_ctx,
        this.chain_video_ctx,
        this.start_offer_ctx,
      ] )
        ctx.scale( dpr, dpr );
    }

    this.initializeGlobalLayoutScaler();

    WebGLUIRuntime.init( this.webgl_ui_main_canvas );
    Vfx2DRuntime.init(
      this.vfx_main_ui_back_canvas,
      this.vfx_main_ui_front_canvas,
      this.vfx_overlay_back_canvas,
      this.vfx_overlay_front_canvas,
    );

    ScreenManager.addOnChangeListener( () => { this.current_screen_painted_once = false; } );

    // handleViewportResize ignores shrinks while a text field is focused (soft
    // keyboard); re-check once it loses focus in case the viewport really changed.
    document.addEventListener( "focusout", () =>
    {
      this.scheduleViewportResizeCheck();
      // `updateTelegramLayout` also waits for the field: re-read the insets once focus has moved.
      if ( this.layout_deferred_by_input )
        window.setTimeout( () => this.updateTelegramLayout(), 300 );
    } );

    GraphicsSettings.subscribe( () =>
    {
      if ( Game.dots_per_pixel !== effectiveUiPixelRatio( GraphicsSettings.profile() ) )
        this.handleViewportResize();
    } );
  }

  private static isTextInputFocused(): boolean
  {
    const active = document.activeElement as HTMLElement | null;
    return !!active
      && ( active.tagName === "INPUT" || active.tagName === "TEXTAREA" || active.isContentEditable );
  }

  static getScreenWidth(): number { return this.SCREEN_WIDTH; }
  static getScreenHeight(): number { return this.SCREEN_HEIGHT; }
  static getDpr(): number { return this.dots_per_pixel; }
  static getGameX(): number { return this.instance.game_x; }

  // The overlay VFX canvases (coin-fly, confetti, fireworks, ...) are
  // normally shown/hidden by OverlayManager as part of its own overlay
  // stack. Screens that play VFX without going through OverlayManager (e.g.
  // AdventureBoxOpenPopup) need to unhide them themselves.
  // The three.js canvas is never part of ScreenManager's stack, so after
  // leaving the game it kept displaying the last rendered frame under every
  // 2D screen; any unpainted pixel above it showed stale game colors. Hiding
  // it also releases a full-screen compositor surface on non-game screens.
  public setGameSceneCanvasVisible( visible: boolean ): void
  {
    const canvas = Renderer.renderer?.domElement;
    if ( !canvas )
      return;

    const display = visible ? "block" : "none";
    if ( canvas.style.display !== display )
      canvas.style.display = display;

    Renderer.setBufferReleased( !visible );
  }

  public setVfxOverlayCanvasesVisible( visible: boolean ): void
  {
    this.setVfxLayerShown( this.vfx_overlay_back_canvas, visible );
    this.setVfxLayerShown( this.vfx_overlay_front_canvas, visible );
  }

  private releasableVfxCanvases(): HTMLCanvasElement[]
  {
    return [
      this.vfx_main_ui_back_canvas, this.vfx_main_ui_front_canvas, this.webgl_ui_main_canvas,
      this.vfx_overlay_back_canvas, this.vfx_overlay_front_canvas,
    ];
  }

  private setVfxLayerShown( canvas: HTMLCanvasElement, shown: boolean ): void
  {
    if ( isMemoryConstrained() )
    {
      const target_w = shown ? Math.round( Game.getDpr() * Game.getScreenWidth() ) : 1;
      const target_h = shown ? Math.round( Game.getDpr() * Game.getScreenHeight() ) : 1;
      if ( canvas.width !== target_w || canvas.height !== target_h )
      {
        canvas.width = target_w;
        canvas.height = target_h;
        if ( shown )
        {
          if ( canvas === this.webgl_ui_main_canvas )
            WebGLUIRuntime.resize();
          else
            Vfx2DRuntime.handleCanvasResize();
        }
      }
    }

    const display = shown ? "block" : "none";
    if ( canvas.style.display !== display )
      canvas.style.display = display;
  }

  public releaseScreenCanvas( canvas: HTMLCanvasElement ): void
  {
    if ( !isMemoryConstrained() || ( canvas.width === 1 && canvas.height === 1 ) )
      return;

    canvas.width = 1;
    canvas.height = 1;
  }

  public static async initializeSafeAreas(): Promise<void>
  {
    const safe_area_insets = TelegramAPISettings.getSafeAreaInsets();
    Game.SAFE_AREA_TOP = safe_area_insets.top;
    Game.SAFE_AREA_BOTTOM = safe_area_insets.bottom;
    Game.SAFE_AREA_LEFT = safe_area_insets.left;
    Game.SAFE_AREA_RIGHT = safe_area_insets.right;
  }

  public updateTelegramLayout(): void
  {
    const previous_bottom = Game.SAFE_AREA_BOTTOM;
    const previous_top = Game.SAFE_AREA_TOP;
    const insets = TelegramAPISettings.getSafeAreaInsets();
    // On Android the soft keyboard brings the hidden navigation bar back for a while, and the
    // bottom inset grows by its height. Re-laying the navigation out for it lifted the bar
    // under the dialog; the layout is checked again once the field loses focus.
    if ( Game.isTextInputFocused() && insets.bottom !== previous_bottom )
    {
      this.layout_deferred_by_input = true;
      return;
    }
    this.layout_deferred_by_input = false;
    Game.SAFE_AREA_TOP = insets.top;
    Game.SAFE_AREA_BOTTOM = insets.bottom;
    Game.SAFE_AREA_LEFT = insets.left;
    Game.SAFE_AREA_RIGHT = insets.right;
    const content_origin = new Vector2( insets.left, insets.top );
    this.game_ui?.setPixelPos( content_origin, true );
    this.menu_screen?.setPixelPos( content_origin, true );
    this.shop_screen?.setPixelPos( content_origin, true );
    this.leaderboard_screen?.setPixelPos( content_origin, true );
    this.daily_tasks_screen?.setPixelPos( content_origin, true );
    this.feedback_screen?.setPixelPos( content_origin, true );
    // Adventure is intentionally edge-to-edge: its artwork fills the physical
    // canvas and does not use the shared safe-area content origin.
    this.adventure_screen?.setPixelPos( new Vector2( 0, 0 ), true );
    this.daily_challenge_screen?.setPixelPos( content_origin, true );
    this.chain_offer_screen?.setPixelPos( content_origin, true );
    this.one_plus_one_offer_screen?.setPixelPos( content_origin, true );
    this.one_plus_two_offer_screen?.setPixelPos( content_origin, true );
    this.start_offer_screen?.setPixelPos( content_origin, true );
    this.chain_video_screen?.setPixelPos( content_origin, true );
    this.updateNavigationCanvas();
    if ( this.navigation_block && previous_bottom !== insets.bottom )
    {
      this.navigation_block.destroy();
      const navigation_height = LayoutScaler.heightFromDesign( 400 ) + insets.bottom;
      this.navigation_block = this.createNavigationBlock(
        this.uiContext( DOM_UI_KEYS.NAVIGATION, this.navigation_ctx ),
        insets.left,
        LayoutScaler.yFromDesign( 0 ),
        Game.getContentWidth(),
        navigation_height
      );
    }
    this.navigation_block?.updateScreenBlock();

    // Screens are built for the content height between the insets: a pooled one built before
    // the change kept its old height under the rebuilt (taller) navigation, so the leaderboard
    // came back with its bottom under the bar. Same as a viewport resize: rebuild them.
    if ( previous_top !== insets.top || previous_bottom !== insets.bottom )
      this.rebuildScreensForNewLayout( true );

    this.scheduleViewportResizeCheck();
    this.renderAll();
  }

  private layout_deferred_by_input = false;

  private rebuildScreensForNewLayout( size_changed: boolean ): void
  {
    const current = ScreenManager.getCurrentScreen();

    if ( current === Screens.GAME )
    {
      // A running level cannot be rebuilt without restarting it, so the game
      // screen only gets its canvases and camera resized; the reallocated UI
      // canvas is blank until the tree repaints.
      this.game_ui?.forceRedraw();
    }
    else if ( current && !this.isLoadingGameScreenShown() && !this.loading_screen )
    {
      // Pooled screens were built for the old density; the next visit builds them anew.
      ScreenManager.dropPooledScreens();
      if ( size_changed )
        void ScreenManager.showScreen( current );
      else
        ( ScreenManager.getCurrentUi() as { invalidateSubtree?: () => void } | null )?.invalidateSubtree?.();
    }
  }

  private viewport_resize_timer: number | null = null;

  private scheduleViewportResizeCheck(): void
  {
    if ( TestLevelBridge.isBatchActive() )
      return;

    if ( this.viewport_resize_timer !== null )
      window.clearTimeout( this.viewport_resize_timer );

    this.viewport_resize_timer = window.setTimeout( () =>
    {
      this.viewport_resize_timer = null;
      this.handleViewportResize();
    }, 300 );
  }

  // The whole canvas stack is sized once in the constructor, but the real
  // viewport can keep changing after startup: Safari collapses its toolbar,
  // Telegram expands the webapp. Every viewport row the stack no longer covers
  // shows the bare #0039B9 body background as a solid stripe, so the stack has
  // to follow the viewport.
  private handleViewportResize(): void
  {
    // The browser viewport is the authority for canvas coverage: some Telegram
    // clients under-report viewportHeight while the webview itself is taller,
    // and every webview row the stack does not cover shows the bare body
    // background regardless of what Telegram considers visible.
    // ceil: a fractional visual-viewport height rounded down would leave a
    // sub-pixel row of background visible along the bottom edge.
    const viewport_w = Math.ceil( window.visualViewport?.width ?? window.innerWidth );
    const viewport_h = Math.ceil( window.visualViewport?.height ?? window.innerHeight );

    if ( viewport_w < 50 || viewport_h < 50 )
      return;

    const horizontal_screen = viewport_w / viewport_h > Game.HORIZONTAL_W_TO_H_RATIO;
    const screen_width = horizontal_screen
      ? Math.round( viewport_h * Game.HORIZONTAL_W_TO_H_RATIO )
      : viewport_w;
    const screen_height = viewport_h;
    const dpr = effectiveUiPixelRatio( GraphicsSettings.profile() );

    // The Android soft keyboard shrinks the WebView (edge-to-edge, no
    // windowSoftInputMode). Relayouting to that height squashes every screen
    // and lifts the navigation bar; the keyboard should just cover the bottom.
    // The fixed-position canvases keep their size and extend under it.
    // When the field loses focus, the focusout listener re-checks.
    if ( Game.isTextInputFocused()
      && viewport_h < Game.SCREEN_HEIGHT
      && dpr === Game.dots_per_pixel )
      return;

    if ( screen_width === Game.SCREEN_WIDTH
      && screen_height === Game.SCREEN_HEIGHT
      && dpr === Game.dots_per_pixel )
      return;

    // Only the pixel density changed (the LOW graphics profile renders the UI at
    // ui_render_scale): the layout is the same, so the canvases get new buffers but
    // the screen and the navigation are repainted, not rebuilt. Rebuilding them
    // made the whole menu jump behind the settings dialog.
    const size_changed = screen_width !== Game.SCREEN_WIDTH || screen_height !== Game.SCREEN_HEIGHT;

    Game.SCREEN_WIDTH = screen_width;
    Game.SCREEN_HEIGHT = screen_height;
    Game.dots_per_pixel = dpr;
    this.game_x = horizontal_screen ? Math.round( ( viewport_w - screen_width ) / 2 ) : 0;

    const buffer_w = Math.round( dpr * screen_width );
    const buffer_h = Math.round( dpr * screen_height );

    const all_canvases = [
      this.vfx_main_ui_back_canvas, this.vfx_main_ui_front_canvas, this.webgl_ui_main_canvas, this.game_canvas, this.adventure_canvas,
      this.daily_challenge_canvas, this.chain_offer_canvas, this.one_plus_one_offer_canvas, this.chain_video_canvas,
      this.one_plus_two_offer_canvas, this.start_offer_canvas, this.tooltip_canvas,
      this.menu_canvas, this.shop_canvas, this.leaderboard_canvas,
      this.navigation_canvas, this.feedback_canvas, this.dialog_canvas,
      this.vfx_overlay_back_canvas, this.vfx_overlay_front_canvas, this.overlay_canvas,
    ];

    for ( const canvas of all_canvases )
    {
      canvas.style.left = `${this.game_x}px`;
      canvas.style.width = `${screen_width}px`;
      canvas.style.height = `${screen_height}px`;
    }

    const full_screen_2d = [
      { canvas: this.tooltip_canvas, ctx: this.tooltip_ctx },
      { canvas: this.dialog_canvas, ctx: this.dialog_ctx },
      { canvas: this.overlay_canvas, ctx: this.overlay_ctx },
    ];

    for ( const { canvas, ctx } of full_screen_2d )
    {
      canvas.width = buffer_w;
      canvas.height = buffer_h;
      ctx.setTransform( 1, 0, 0, 1, 0, 0 );
      if ( dpr !== 1 )
        ctx.scale( dpr, dpr );
    }

    const constrained = isMemoryConstrained();
    for ( const canvas of this.releasableVfxCanvases() )
    {
      if ( constrained && canvas.style.display !== "block" )
        continue;
      canvas.width = buffer_w;
      canvas.height = buffer_h;
    }
    Vfx2DRuntime.handleCanvasResize();
    if ( constrained && this.webgl_ui_main_canvas.style.display === "block" )
      WebGLUIRuntime.resize();

    const screen_canvases = [
      this.game_canvas, this.adventure_canvas, this.daily_challenge_canvas,
      this.chain_offer_canvas, this.one_plus_one_offer_canvas, this.one_plus_two_offer_canvas, this.chain_video_canvas,
      this.start_offer_canvas, this.menu_canvas, this.shop_canvas,
      this.leaderboard_canvas, this.feedback_canvas,
    ];

    for ( const canvas of screen_canvases )
    {
      if ( canvas.width > 1 )
        this.allocateScreenCanvas( canvas );
    }

    this.initializeGlobalLayoutScaler();

    DialogManager.setScreenSize( screen_width, screen_height );
    OverlayManager.setScreenSize( screen_width, screen_height );
    CoinFlightHud.setScreenSize( this.game_x, screen_width, screen_height, dpr );

    Renderer.resize();

    this.updateNavigationCanvas();

    if ( this.navigation_block && !size_changed )
    {
      this.navigation_block.invalidateSubtree();
    }
    else if ( this.navigation_block )
    {
      this.navigation_block.destroy();
      this.navigation_block = this.createNavigationBlock(
        this.uiContext( DOM_UI_KEYS.NAVIGATION, this.navigation_ctx ),
        Game.SAFE_AREA_LEFT,
        LayoutScaler.yFromDesign( 0 ),
        Game.getContentWidth(),
        LayoutScaler.heightFromDesign( 400 ) + Game.SAFE_AREA_BOTTOM
      );
      this.navigation_block.updateScreenBlock();
    }

    if ( this.loading_screen || this.loading_game_screen )
    {
      this.setupLoadingCanvas( this.loading_screen !== undefined );
      if ( this.loading_screen )
      {
        this.loading_screen.cleanup();
        this.loading_screen = new LoadingScreen(
          this.uiContext( DOM_UI_KEYS.LOADING, this.loading_ctx ),
          0,
          0,
          screen_width,
          screen_height + Game.safeAreaBottom()
        );
        this.loading_screen.mountDomHost( this.loading_canvas );
      }
      if ( this.loading_game_screen )
      {
        this.loading_game_screen.cleanup();
        this.loading_game_screen = new LoadingGameScreen( this.uiContext( DOM_UI_KEYS.LOADING, this.loading_ctx ), 0, 0, screen_width, screen_height, this.loading_game_variant );
        this.loading_game_screen.mountDomHost( this.loading_canvas );
      }
    }

    this.rebuildScreensForNewLayout( size_changed );

    this.renderAll();
  }

  static safeAreaTop(): number { return this.SAFE_AREA_TOP; }
  static safeAreaBottom(): number { return this.SAFE_AREA_BOTTOM; }
  static safeAreaLeft(): number { return this.SAFE_AREA_LEFT; }
  static safeAreaRight(): number { return this.SAFE_AREA_RIGHT; }

  private initializeGlobalLayoutScaler(): void
  {
    console.table( {
      screen: { w: screen.width, h: screen.height },
      inner: { w: innerWidth, h: innerHeight },
      vv: { w: visualViewport?.width, h: visualViewport?.height },
      game: { w: Game.SCREEN_WIDTH, h: Game.SCREEN_HEIGHT },
      dpr: Game.dots_per_pixel
    } );
    const scaler = new LayoutScalerLocal( Game.SCREEN_WIDTH, Game.SCREEN_HEIGHT );

    globalThis.LayoutScaler = {
      widthFromDesign: ( design_px: number ) => scaler.widthFromDesign( design_px ),
      heightFromDesign: ( design_px: number ) => scaler.heightFromDesign( design_px ),
      xFromDesign: ( design_x_at_base: number ) => scaler.xFromDesign( design_x_at_base ),
      yFromDesign: ( design_y_at_base: number ) => scaler.yFromDesign( design_y_at_base ),
      widthFromDesignAdaptive: ( design_w_at_base: number ) => scaler.widthFromDesignAdaptive( design_w_at_base ),
      heightFromDesignAdaptive: ( design_h_at_base: number ) => scaler.heightFromDesignAdaptive( design_h_at_base ),
      xFromLayouts: ( x_min: number, x_max: number ) => scaler.xFromLayouts( x_min, x_max ),
      yFromLayouts: ( y_min: number, y_max: number ) => scaler.yFromLayouts( y_min, y_max ),
      widthFromLayouts: ( w_min: number, w_max: number ) => scaler.widthFromLayouts( w_min, w_max ),
      heightFromLayouts: ( h_min: number, h_max: number ) => scaler.heightFromLayouts( h_min, h_max ),
      widthFromHeightLayouts: ( w_min: number, w_max: number ) => scaler.widthFromHeightLayouts( w_min, w_max ),
      cornerRadiusFromDesign: ( design_radius: number ) => scaler.cornerRadiusFromDesign( design_radius ),
      getScale: () => scaler.getScale(),
      getVisibleDesignWidth: () => scaler.getVisibleDesignWidth(),
      getVisibleDesignHeight: () => scaler.getVisibleDesignHeight(),
      textSizeFromDesign: ( text_size: number ) => scaler.textSizeFromDesign( text_size ),
      textBorderSizeFromDesign: ( border_size: number ) => scaler.textBorderSizeFromDesign( border_size ),
    };
  }

  static isMobile(): boolean { return this.is_mobile; }

  // A brand-new install that hasn't finished the level-1 tutorial yet and
  // hasn't progressed past level 1 — used to route straight into the game
  // screen on launch instead of the menu, so the tutorial can run.
  static isFirstTimePlayer(): boolean
  {
    const stats = StatisticsStorage.load();
    return !stats.tutorial_completed && stats.level === 1;
  }

  private screenContext( canvas: HTMLCanvasElement, key: string ): CanvasRenderingContext2D
  {
    if ( resolveUiBackend( key ) === "dom" )
      return createNullCanvasContext( canvas );

    const ctx = canvas.getContext( "2d" );
    if ( !ctx )
      throw new Error( "Canvas 2D context not supported." );
    markCanvasHasContext( canvas );
    return ctx;
  }

  private uiContext( key: string, real: CanvasRenderingContext2D ): CanvasRenderingContext2D
  {
    return resolveUiBackend( key ) === "dom" ? createNullCanvasContext( real ) : real;
  }

  private createNavigationBlock( ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number ): NavigationBlock
  {
    const block = new NavigationBlock( ctx, x, y, width, height );
    block.mountDomHost( this.navigation_canvas );
    return block;
  }

  public initializeGameScreen(): { screen: GameScreen, ui: GameUI }
  {
    const game_screen = new GameScreen();
    this.game_ui = new GameUI( this.uiContext( Screens.GAME, this.game_ctx ), Game.safeAreaLeft(), Game.safeAreaTop(), Game.getContentWidth(), Game.getContentHeight(), game_screen );
    this.game_ui.mountDomHost( this.game_canvas );

    return { screen: game_screen, ui: this.game_ui };
  }

  public initializePrizeShotScreen(): { screen: PrizeShotScreen, ui: PrizeShotUI }
  {
    const screen = new PrizeShotScreen();
    const ui = new PrizeShotUI( this.uiContext( Screens.GAME, this.game_ctx ), Game.safeAreaLeft(), Game.safeAreaTop(), Game.getContentWidth(), Game.getContentHeight(), screen );
    ui.mountDomHost( this.game_canvas );
    this.game_ui = ui;

    return { screen, ui };
  }

  public initializeMenuScreen( defer_intro = false ): MenuScreen
  {
    const menu_screen = new MenuScreen( this.uiContext( Screens.MENU, this.menu_ctx ), Game.safeAreaLeft(), Game.safeAreaTop(), Game.getContentWidth(), Game.getContentHeight(), defer_intro );
    menu_screen.mountDomHost( this.menu_canvas );

    this.menu_screen = menu_screen;
    return menu_screen;
  }

  public initializeShopScreen(): ShopScreen
  {
    const shop_screen = new ShopScreen( this.uiContext( Screens.SHOP, this.shop_ctx ), Game.safeAreaLeft(), Game.safeAreaTop(), Game.getContentWidth(), Game.getContentHeight() );
    shop_screen.mountDomHost( this.shop_canvas );

    this.shop_screen = shop_screen;
    return shop_screen;
  }

  public initializeLeaderboardScreen(): LeaderboardScreen
  {
    const leaderboard_screen = new LeaderboardScreen( this.uiContext( Screens.LEADERBOARD, this.leaderboard_ctx ), Game.safeAreaLeft(), Game.safeAreaTop(), Game.getContentWidth(), Game.getContentHeight() );
    leaderboard_screen.mountDomHost( this.leaderboard_canvas );

    this.leaderboard_screen = leaderboard_screen;
    return leaderboard_screen;
  }

  public initializeDailyTasksScreen(): DailyTasksScreen
  {
    const daily_tasks_screen = new DailyTasksScreen(
      this.uiContext( Screens.DAILY_TASKS, this.menu_ctx ),
      Game.safeAreaLeft(),
      Game.safeAreaTop(),
      Game.getContentWidth(),
      Game.getContentHeight()
    );
    daily_tasks_screen.mountDomHost( this.menu_canvas );

    this.daily_tasks_screen = daily_tasks_screen;
    return daily_tasks_screen;
  }

  public initializeFeedbackScreen(): FeedbackScreen
  {
    const feedback_screen = new FeedbackScreen( this.uiContext( Screens.FEEDBACK, this.feedback_ctx ), Game.safeAreaLeft(), Game.safeAreaTop(), Game.getContentWidth(), Game.getContentHeight() );
    feedback_screen.mountDomHost( this.feedback_canvas );

    this.feedback_screen = feedback_screen;
    return feedback_screen;
  }

  public initializeAdventureScreen( tower_state: WinStreakTowerDto ): AdventureScreen
  {
    const adventure_screen = new AdventureScreen(
      this.uiContext( Screens.ADVENTURE, this.adventure_ctx ),
      0,
      0,
      Game.screenW(),
      Game.screenH(),
      tower_state
    );
    adventure_screen.mountDomHost( this.adventure_canvas );

    this.adventure_screen = adventure_screen;
    return adventure_screen;
  }

  public initializeDailyChallengeScreen(): DailyChallengeScreen
  {
    const daily_challenge_screen = new DailyChallengeScreen( this.uiContext( Screens.DAILY_CHALLENGE, this.daily_challenge_ctx ), Game.safeAreaLeft(), Game.safeAreaTop(), Game.getContentWidth(), Game.getContentHeight() );
    daily_challenge_screen.mountDomHost( this.daily_challenge_canvas );

    this.daily_challenge_screen = daily_challenge_screen;
    return daily_challenge_screen;
  }

  public initializeChainOfferScreen(): ChainOfferScreen
  {
    const chain_offer_screen = new ChainOfferScreen( this.uiContext( Screens.CHAIN_OFFER, this.chain_offer_ctx ), Game.safeAreaLeft(), Game.safeAreaTop(), Game.getContentWidth(), Game.getContentHeight() );
    chain_offer_screen.mountDomHost( this.chain_offer_canvas );

    this.chain_offer_screen = chain_offer_screen;
    return chain_offer_screen;
  }

  public initializeOnePlusOneOfferScreen( params?: Partial<IOnePlusOneOfferParams> ): OnePlusOneOfferScreen
  {
    // Opened from the lobby button with the resolved placement; a bare open
    // (debug console) falls back to whatever the server resolved for the
    // entry point. Without an offer the screen just shows its frame and
    // returns to the lobby.
    const placement = OfferService.getPlacementOffer( ONE_PLUS_ONE_PLACEMENT );
    const offer = params?.offer ?? ( isOnePlusOneOffer( placement?.offer ) ? placement!.offer : null );
    const offer_params: IOnePlusOneOfferParams = {
      offer,
      placement_id: params?.placement_id ?? ONE_PLUS_ONE_PLACEMENT,
    };

    const one_plus_one_offer_screen = new OnePlusOneOfferScreen(
      this.uiContext( Screens.ONE_PLUS_ONE_OFFER, this.one_plus_one_offer_ctx ),
      Game.safeAreaLeft(),
      Game.safeAreaTop(),
      Game.getContentWidth(),
      Game.getContentHeight(),
      offer_params
    );
    one_plus_one_offer_screen.mountDomHost( this.one_plus_one_offer_canvas );

    this.one_plus_one_offer_screen = one_plus_one_offer_screen;
    return one_plus_one_offer_screen;
  }

  public initializeOnePlusTwoOfferScreen( params?: Partial<IOnePlusTwoOfferParams> ): OnePlusTwoOfferScreen
  {
    const placement = OfferService.getPlacementOffer( ONE_PLUS_TWO_PLACEMENT );
    const offer = params?.offer ?? ( isOnePlusOneOffer( placement?.offer ) ? placement!.offer : null );
    const offer_params: IOnePlusTwoOfferParams = {
      offer,
      placement_id: params?.placement_id ?? ONE_PLUS_TWO_PLACEMENT,
    };

    const one_plus_two_offer_screen = new OnePlusTwoOfferScreen(
      this.uiContext( Screens.ONE_PLUS_TWO_OFFER, this.one_plus_two_offer_ctx ),
      Game.safeAreaLeft(),
      Game.safeAreaTop(),
      Game.getContentWidth(),
      Game.getContentHeight(),
      offer_params
    );
    one_plus_two_offer_screen.mountDomHost( this.one_plus_two_offer_canvas );

    this.one_plus_two_offer_screen = one_plus_two_offer_screen;
    return one_plus_two_offer_screen;
  }

  public initializeStartOfferScreen(): StartOfferScreen
  {
    const start_offer_screen = new StartOfferScreen( this.start_offer_ctx, Game.safeAreaLeft(), Game.safeAreaTop(), Game.getContentWidth(), Game.getContentHeight() );

    this.start_offer_screen = start_offer_screen;
    return start_offer_screen;
  }

  public initializeChainVideoScreen(): ChainVideoScreen
  {
    const chain_video_screen = new ChainVideoScreen( this.chain_video_ctx, Game.safeAreaLeft(), Game.safeAreaTop(), Game.getContentWidth(), Game.getContentHeight() );

    this.chain_video_screen = chain_video_screen;
    return chain_video_screen;
  }

  public openSettingsDialog(): void
  {
    SettingsDialog.open();
  }

  public getGameX(): number
  {
    return this.game_x;
  }

  public setGameUICanvasHeight( css_height: number ): boolean
  {
    const dpr = Game.getDpr();
    const target_h = Math.round( dpr * css_height );

    if ( this.game_canvas.height === target_h )
      return false;

    this.game_canvas.height = target_h;
    this.game_canvas.style.height = `${css_height}px`;
    this.game_ctx.setTransform( 1, 0, 0, 1, 0, 0 );
    if ( dpr !== 1 )
      this.game_ctx.scale( dpr, dpr );

    return true;
  }

  public allocateScreenCanvas( canvas: HTMLCanvasElement ): void
  {
    const dpr = Game.getDpr();
    const target_w = Math.round( dpr * Game.getScreenWidth() );
    const target_h = Math.round( dpr * Game.getScreenHeight() );

    if ( canvas.width === target_w && canvas.height === target_h )
      return;

    canvas.width = target_w;
    canvas.height = target_h;

    const ctx = canvasHasContext( canvas ) ? canvas.getContext( "2d" ) : null;
    if ( ctx )
    {
      ctx.setTransform( 1, 0, 0, 1, 0, 0 );
      if ( dpr !== 1 )
        ctx.scale( dpr, dpr );
    }
  }

  // Big art a screen draws on its first frame. A decoded bitmap is uploaded to
  // the GPU on its first drawImage, which made the offers' first two frames
  // ~75 ms each; drawing them into the warm-up canvas first moves that cost
  // to while the previous screen is still showing.
  private static readonly SCREEN_GPU_WARMUP: Partial<Record<Screens, ImgID[]>> = {
    [Screens.ONE_PLUS_ONE_OFFER]: [
      ImgID.UI_OnePlusOneOfferSky, ImgID.UI_OnePlusOneOfferFrame, ImgID.UI_OnePlusOneOfferTiger, ImgID.UI_OnePlusOneOfferConfetti,
    ],
    [Screens.ONE_PLUS_TWO_OFFER]: [
      ImgID.UI_OnePlusTwoOfferSky, ImgID.UI_OnePlusTwoOfferTiger, ImgID.UI_OnePlusOneOfferConfetti,
    ],
    [Screens.CHAIN_VIDEO]: [
      ImgID.UI_ChainVideoTopCat, ImgID.UI_ChainOfferConfetti, ImgID.UI_ChainOfferSnakeChain,
      ImgID.UI_ChainOfferSubstrate, ImgID.UI_ChainOfferSubstrateActive,
      // Not tagged for any screen, so the cards used to open with placeholders.
      ImgID.UI_ChainOfferVideoIcon, ImgID.UI_ChainOfferLockIcon, ImgID.UI_ChainVideoTimerBg,
    ],
    [Screens.CHAIN_OFFER]: [
      ImgID.UI_ChainOfferCloud, ImgID.UI_ChainOfferBalloonIcon, ImgID.UI_ChainOfferConfetti, ImgID.UI_ChainOfferSnakeChain,
      ImgID.UI_ChainOfferSubstrate, ImgID.UI_ChainOfferSubstrateActive, ImgID.UI_ChainOfferTimerSubstrate,
      ImgID.UI_ChainOfferGreenBtn, ImgID.UI_ChainOfferVideoIcon, ImgID.UI_ChainOfferLockIcon,
    ],
  };

  private static readonly PRICE_PREFETCH_WAIT_MS = 400;

  /**
   * Asks the store for the paid step's price while the previous screen is
   * still up. When it answered a few frames after the offer opened, the
   * columns were rebuilt mid-open and the device stalled ~190 ms around it.
   * Waits a bounded time: a slow store only means the old '…' then price.
   */
  private static async prefetchOnePlusPrice( placement_id: string ): Promise<void>
  {
    const offer = OfferService.getPlacementOffer( placement_id )?.offer;
    const paid = getOnePlusOneState( isOnePlusOneOffer( offer ) ? offer : null )?.steps.find( step => step.status === 'paid' );
    const product = paid?.step.products ? getPlatformProductConfig( paid.step.products ) : null;
    if ( !product || IapPlugin.peekProductDetails( product.productId ) ) return;

    await Promise.race( [
      IapPlugin.getProductDetails( product.productId ).catch( () => null ),
      new Promise( resolve => setTimeout( resolve, Game.PRICE_PREFETCH_WAIT_MS ) ),
    ] );
  }

  // Leaderboard art, warmed from the menu rather than in prepareScreenImages.
  private static readonly LEADERBOARD_IDLE_WARMUP: ImgID[] = [
    ImgID.UI_LeaderboardTopBg, ImgID.UI_LeaderboardInfoButton, ImgID.UI_LeaderboardSpinner,
    ImgID.UI_LeaderboardSelectedMiddleTab, ImgID.UI_LeaderboardSelectedSideTab,
    ImgID.UI_LeaderboardUnselectedMiddleTab, ImgID.UI_LeaderboardUnselectedSideTab,
    ImgID.UI_LeaderboardSubstratePlayerNew, ImgID.UI_LeaderboardSubstrateYouNew, ImgID.UI_LeaderboardPlayerAvatarSub,
    ImgID.UI_LeaderboardStarBlue, ImgID.UI_LeaderboardStarBrown, ImgID.UI_LeaderboardStarOrange,
    ImgID.UI_LeaderboardEye, ImgID.ProfileStarIcon, ImgID.Flag_unknown,
  ];

  private static readonly MENU_PREWARM_DELAY_MS = 2000;
  private menu_prewarm_token = 0;

  /**
   * While the player sits in the menu, loads the leaderboard's art and uploads it
   * (plus the cached board's avatars and flags) to the GPU one image per idle
   * slot. On device its first open otherwise spent ~85 ms of its first frame on
   * those uploads, and warming them in prepareScreenImages delayed the tap by
   * ~280 ms instead.
   */
  private scheduleMenuIdlePrewarm(): void
  {
    const token = ++this.menu_prewarm_token;
    const still_in_menu = (): boolean => token === this.menu_prewarm_token && ScreenManager.getCurrentScreen() === Screens.MENU;
    setTimeout( async () =>
    {
      if ( !still_in_menu() ) return;
      try
      {
        // Login and the board ahead of the tap: the open then shows the board in its first
        // frame instead of waiting ~0.5 s on auth, registration and the request.
        // The network goes now; decodes and uploads wait for a BackgroundWork slot each.
        const board = LeaderboardBoardService.prefetch();
        await BackgroundWork.run( "leaderboard images", () => still_in_menu() ? ImagesHolder.preloadScreens( [ Screens.LEADERBOARD ] ) : undefined );
        await board;
        // The country tab's board after the global one: its catch-up reads the global board.
        void LeaderboardCountryBoardService.prefetch();
        if ( !still_in_menu() ) return;
        await BackgroundWork.run( "prize shot prefetch", () => still_in_menu() ? PrizeShotScreen.prefetchAssets() : undefined );
        if ( !still_in_menu() ) return;
        const ids = [ ...Game.LEADERBOARD_IDLE_WARMUP, ...LeaderboardScreen.cachedBoardImageIds() ];
        await Promise.all( ids.map( id => ImagesHolder.ensure( id ).catch( () => null ) ) );
        if ( !still_in_menu() ) return;
        await BackgroundWork.run( "leaderboard gpu", () => still_in_menu() ? Promise.all( [ ImagesHolder.warmupGpu( ids ), LeaderboardScreen.prepareRowBackdrop() ] ) : undefined );
      }
      catch ( e )
      {
        console.warn( "[Game] menu idle prewarm failed", e );
      }
    }, Game.MENU_PREWARM_DELAY_MS );
  }

  public async prepareScreenImages( name: Screens ): Promise<void>
  {
    try
    {
      await ImagesHolder.preloadScreens( [ name ], 10 );

      if ( name === Screens.LEADERBOARD )
        await LeaderboardScreen.prepareRowBackdrop();

      const warm = Game.SCREEN_GPU_WARMUP[name];
      if ( warm )
      {
        await Promise.all( warm.map( id => ImagesHolder.ensure( id ).catch( () => null ) ) );
        await ImagesHolder.warmupGpu( warm );
      }

      // Locked (dimmed) and FREE (hue-rotated) offer buttons: their recoloured
      // copies need a readback that cost ~36 ms on the offers' first frame.
      if ( name === Screens.ONE_PLUS_ONE_OFFER || name === Screens.ONE_PLUS_TWO_OFFER )
      {
        UIImage.prewarmVariants( ImgID.UI_OnePlusOneOfferGreenBtn, [ { dimmed: true }, { hue: FREE_BUTTON_HUE_ROTATION } ] );
        await Game.prefetchOnePlusPrice( name === Screens.ONE_PLUS_ONE_OFFER ? ONE_PLUS_ONE_PLACEMENT : ONE_PLUS_TWO_PLACEMENT );
      }
    }
    catch ( e )
    {
      console.warn( `[Game] image preload for screen "${name}" failed`, e );
    }
  }

  private static readonly RETURNS_TO_MENU: ReadonlySet<Screens> = new Set( [
    Screens.DAILY_TASKS,
    Screens.CHAIN_OFFER, Screens.CHAIN_VIDEO, Screens.ONE_PLUS_ONE_OFFER, Screens.ONE_PLUS_TWO_OFFER,
  ] );

  public releaseScreenImages( name: Screens ): void
  {
    if ( localStorage.getItem( "perf_keep_screen_images" ) === "1" )
      return;

    const keep = [ name, Screens.GAME ];
    // The three bottom tabs keep their existing DOM and art while switching between them.
    // Entering a level still follows the usual release policy.
    if ( ScreenManager.isNavigationTab( name ) )
      for ( const tab of ScreenManager.pooledScreenNames() )
        if ( ScreenManager.isNavigationTab( tab ) ) keep.push( tab );
    // Screens that only ever return to the menu keep its art: closing an offer
    // otherwise re-fetched and decoded 23 menu images (10.8 MB) before the menu
    // could show, and the offer sat frozen for a second or more meanwhile.
    if ( Game.RETURNS_TO_MENU.has( name ) )
      keep.push( Screens.MENU );

    ImagesHolder.releaseScreens( keep );
  }

  private updateNavigationCanvas(): void
  {
    const dpr = Game.getDpr();
    const logical_screen_w = Game.getScreenWidth();
    const logical_screen_h = Game.getScreenHeight();

    const height = LayoutScaler.yFromDesign( 400 ) + Game.SAFE_AREA_BOTTOM;

    const target_w = Math.round( dpr * logical_screen_w );
    const target_h = Math.round( dpr * height );

    if ( this.navigation_canvas.width !== target_w || this.navigation_canvas.height !== target_h )
    {
      this.navigation_canvas.width = target_w;
      this.navigation_canvas.height = target_h;
    }

    const y = Game.screenH() - height;

    this.navigation_canvas.style.top = `${y}px`;
    this.navigation_canvas.style.height = `${logical_screen_h - y}px`;

    const ctx = this.navigation_ctx;
    ctx.setTransform( 1, 0, 0, 1, 0, 0 );
    ctx.scale( dpr, dpr );
  }

  private static readonly VFX_HIDE_DELAY_FRAMES = 10;
  private vfx_hide_frames = [ 0, 0, 0, 0, 0 ];

  private current_screen_painted_once = false;

  private updateVfxLayerVisibility( canvas: HTMLCanvasElement, has_effects: boolean, idx: number ): boolean
  {
    if ( has_effects )
    {
      this.vfx_hide_frames[idx] = 0;
      if ( canvas.style.display !== "block" || ( canvas.width === 1 && isMemoryConstrained() ) )
        this.setVfxLayerShown( canvas, true );
      return true;
    }

    if ( canvas.style.display !== "block" )
      return false;

    this.vfx_hide_frames[idx]++;
    if ( this.vfx_hide_frames[idx] < Game.VFX_HIDE_DELAY_FRAMES )
      return true;

    this.setVfxLayerShown( canvas, false );
    return false;
  }

  private renderAll() // TODO: optimize rendering
  {
    const current_screen = ScreenManager.getCurrentScreen();

    // Background screens keep repainting (button press animations, timers)
    // right when a dialog's layers are being composited for the first time —
    // exactly the most bandwidth-hungry frames. They sit behind the dim, so
    // their repaints can wait out the dialog open window; dirty flags survive.
    // The freeze must never swallow the FIRST paint of a freshly shown screen:
    // a dialog opened together with a screen change (rate dialog on the menu,
    // day-completed dialog on daily challenge) would otherwise hold a blank
    // canvas for 400ms, flashing the bare body background under the dialog.
    const dialog_open_freeze = current_screen !== Screens.MENU
      && DialogManager.isAnyDialogOpened()
      && DialogManager.msSinceLastOpen() < 400
      && this.current_screen_painted_once;

    if ( !dialog_open_freeze && current_screen !== null )
      this.current_screen_painted_once = true;

    if ( !dialog_open_freeze )
    if ( current_screen === Screens.GAME ) this.game_ui?.render(); else
    if ( current_screen === Screens.MENU ) this.menu_screen?.render(); else
    if ( current_screen === Screens.SHOP ) this.shop_screen?.render(); else
    if ( current_screen === Screens.LEADERBOARD ) this.leaderboard_screen?.render(); else
    if ( current_screen === Screens.DAILY_TASKS ) this.daily_tasks_screen?.render(); else
    if ( current_screen === Screens.ADVENTURE ) this.adventure_screen?.render(); else
    if ( current_screen === Screens.DAILY_CHALLENGE ) this.daily_challenge_screen?.render(); else
    if ( current_screen === Screens.CHAIN_OFFER ) this.chain_offer_screen?.render(); else
    if ( current_screen === Screens.ONE_PLUS_ONE_OFFER ) this.one_plus_one_offer_screen?.render(); else
    if ( current_screen === Screens.ONE_PLUS_TWO_OFFER ) this.one_plus_two_offer_screen?.render(); else
    if ( current_screen === Screens.CHAIN_VIDEO ) this.chain_video_screen?.render(); else
    if ( current_screen === Screens.START_OFFER ) this.start_offer_screen?.render();

    // Lists that only scrolled this frame repaint just their viewport.
    if ( !dialog_open_freeze )
      UIScrollableContainer.flushPendingRepaints( this.currentScreenUi( current_screen ) );

    if ( !dialog_open_freeze && this.navigation_canvas.style.display === "block" )
      this.navigation_block?.render();
    
    if ( this.updateVfxLayerVisibility( this.webgl_ui_main_canvas, WebGLUIRuntime.hasMainElements(), 0 ) )
      WebGLUIRuntime.renderMain( Game.dots_per_pixel );
    
    if ( this.updateVfxLayerVisibility( this.vfx_main_ui_back_canvas, Vfx2DRuntime.hasMainBackEffects(), 1 ) )
      Vfx2DRuntime.renderMainBack( Game.dots_per_pixel );
    
    if ( this.updateVfxLayerVisibility( this.vfx_main_ui_front_canvas, Vfx2DRuntime.hasMainFrontEffects(), 2 ) )
      Vfx2DRuntime.renderMainFront( Game.dots_per_pixel );

    if ( this.updateVfxLayerVisibility( this.vfx_overlay_back_canvas, Vfx2DRuntime.hasOverlayBackEffects(), 3 ) )
      Vfx2DRuntime.renderOverlayBack( Game.dots_per_pixel );

    if ( this.updateVfxLayerVisibility( this.vfx_overlay_front_canvas, Vfx2DRuntime.hasOverlayFrontEffects(), 4 ) )
      Vfx2DRuntime.renderOverlayFront( Game.dots_per_pixel );

    if ( this.tooltip_canvas.style.display !== "none" )
      TooltipManager.render();

    DialogManager.render();
    OverlayManager.render();
    CoinFlightHud.render();
  }

  // The UI tree renderAll paints for a screen; an identity key here, and the
  // root UiPerfProbe instruments.
  public currentScreenUi( screen: Screens | null ): object | null
  {
    switch ( screen )
    {
      case Screens.GAME: return this.game_ui ?? null;
      case Screens.MENU: return this.menu_screen ?? null;
      case Screens.SHOP: return this.shop_screen ?? null;
      case Screens.LEADERBOARD: return this.leaderboard_screen ?? null;
      case Screens.DAILY_TASKS: return this.daily_tasks_screen ?? null;
      case Screens.ADVENTURE: return this.adventure_screen ?? null;
      case Screens.DAILY_CHALLENGE: return this.daily_challenge_screen ?? null;
      case Screens.CHAIN_OFFER: return this.chain_offer_screen ?? null;
      case Screens.ONE_PLUS_ONE_OFFER: return this.one_plus_one_offer_screen ?? null;
      case Screens.ONE_PLUS_TWO_OFFER: return this.one_plus_two_offer_screen ?? null;
      case Screens.CHAIN_VIDEO: return this.chain_video_screen ?? null;
      default: return null;
    }
  }

  private animationsUpdateAll( now: number )
  {
    const current_screen = ScreenManager.getCurrentScreen();

    // Before the screens: it hides/shows the menu's own coin counter, and that
    // change must bubble through the menu's update to be painted this frame.
    CoinFlightHud.update( now );

    if ( current_screen === Screens.GAME ) this.game_ui?.update( now ); else
    if ( current_screen === Screens.MENU ) this.menu_screen?.update( now ); else
    if ( current_screen === Screens.SHOP ) this.shop_screen?.update( now ); else
    if ( current_screen === Screens.LEADERBOARD ) this.leaderboard_screen?.update( now ); else
    if ( current_screen === Screens.DAILY_TASKS ) this.daily_tasks_screen?.update( now ); else
    if ( current_screen === Screens.ADVENTURE ) this.adventure_screen?.update( now ); else
    if ( current_screen === Screens.DAILY_CHALLENGE ) this.daily_challenge_screen?.update( now ); else
    if ( current_screen === Screens.CHAIN_OFFER ) this.chain_offer_screen?.update( now ); else
    if ( current_screen === Screens.ONE_PLUS_ONE_OFFER ) this.one_plus_one_offer_screen?.update( now ); else
    if ( current_screen === Screens.ONE_PLUS_TWO_OFFER ) this.one_plus_two_offer_screen?.update( now ); else
    if ( current_screen === Screens.CHAIN_VIDEO ) this.chain_video_screen?.update( now ); else
    if ( current_screen === Screens.START_OFFER ) this.start_offer_screen?.update( now );

    this.navigation_block?.update( now );
    OverlayManager.update( now );
    DialogManager.update( now );
    TooltipManager.update( now );
    WebGLUIRuntime.update( now );
    Vfx2DRuntime.update( now );
  }

  static screenW() { return this.SCREEN_WIDTH; }
  static screenH() { return this.SCREEN_HEIGHT; }

  private static game_ui_paint_revision = 0;

  static markGameUIPainted(): void { this.game_ui_paint_revision++; }
  static gameUIPaintRevision(): number { return this.game_ui_paint_revision; }

  static getContentWidth(): number { return this.SCREEN_WIDTH - this.SAFE_AREA_LEFT - this.SAFE_AREA_RIGHT; }
  static getContentHeight(): number { return this.SCREEN_HEIGHT - this.SAFE_AREA_TOP - this.SAFE_AREA_BOTTOM; }

  public showNavigationBlock()
  {
    this.navigation_canvas.style.display = "block";
  }

  public hideNavigationBlock()
  {
    this.navigation_canvas.style.display = "none";
  }

  public setNavigationClickable( clickable: boolean ): void
  {
    this.navigation_block?.setClickable( clickable );
  }
  
  public updateNavigationBlock(): void
  {
    this.navigation_block?.updateScreenBlock();
  }

  public async init(): Promise<void>
  {
    const first_screen_is_game = TestLevelBridge.getLevel() !== null || TestLevelBridge.isPendingSingle() || Game.isFirstTimePlayer();
    StartupProgress.setFirstScreenIsGame( first_screen_is_game );
    fillAllImagesInfo();
    fillAllResourcesInfo();

    await PhysicsWorld.init();
    await NineSliceDrawer.init();
    // await AssetsSizeLoader.load();

    await ImagesHolder.preloadImages( [
      ImgID.UI_LoadingBg,
      ImgID.UI_LoadingProgressBarBase,
      ImgID.UI_LoadingProgressBarFill,
      ImgID.UI_LoadingLogoRoyal,
      ImgID.UI_LoadingLogoSmash,
      ImgID.UI_LoadingLogoCrown,
      ImgID.UI_LoadingLogoCrowl,
      ImgID.UI_LoadingLogoSubstrate,
      ImgID.UI_CapsuleBlue,
      ImgID.AvatarCircle,
      ImgID.UI_ConnectionFailedIcon,
      ImgID.UI_GreenFrameBtn,
      ImgID.UI_SettingsDialogCloseBtn,
    ] );

    DialogManager.init( this.uiContext( DOM_UI_KEYS.DIALOG, this.dialog_ctx ), Game.getScreenWidth(), Game.getScreenHeight() );
    TooltipManager.init(
      this.uiContext( DOM_UI_KEYS.TOOLTIP, this.tooltip_ctx ),
      Game.SCREEN_WIDTH,
      Game.SCREEN_HEIGHT,
      p => ScreenManager.replayTapOnCurrentUi( p, this.tooltip_canvas )
    );
    NetworkManager.onUiReady();
    ScreenManager.addOnChangeListener( next =>
    {
      if ( next !== Screens.MENU ) return;
      // Once per session, on the switch from the loading screen: that frame is
      // heavy anyway, while a screen's first frame is where the player feels it.
      warmCanvas2DShaders();
      this.scheduleMenuIdlePrewarm();
    } );

    if ( ( window as any ).zero_loading_ref )
    {
      cancelAnimationFrame( ( window as any ).zero_loading_ref );
      ( window as any ).zero_loading_ref = null;
    }

    this.setupLoadingCanvas( true );
    this.loading_screen = new LoadingScreen(
      this.uiContext( DOM_UI_KEYS.LOADING, this.loading_ctx ),
      0,
      0,
      Game.getScreenWidth(),
      Game.getScreenHeight() + Game.safeAreaBottom()
    );
    this.loading_screen.mountDomHost( this.loading_canvas );
    this.loading_screen.render();
    const loading_screen_started_at = performance.now();

    // this.startRenderLoop();

    const scene = SceneManager.init();
    Renderer.init();

    const cameraController = new CameraController();
    SceneManager.camera = cameraController.getCamera();

    const FIXED_DT = 1 / 60;
    let accumulator = 0;
    let last_stats_log = 0;

    FpsOverlay.init();
    FpsOverlay.setCurrentScreenProvider( () => ScreenManager.getCurrentScreen() ?? "none" );


    const loop = () =>
    {
      requestAnimationFrame( loop );

      if ( TriangleBench.isActive() )
        return;

      const frame_start = performance.now();
      PerfSession.onFrameStart( frame_start );

      // getDelta() викликаємо завжди, щоб dt не накопичувався в кліку
      const dt = Math.min( Renderer.clock.getDelta(), 0.1 );

      let steps = 0;

      if ( !SimulationContext.enabled ) // [SIM]
      {
        accumulator += dt;
        PhysicsWorld.resetTimers();

        const physics_start = performance.now();
        const step_dt = Game.physics_slow_mode ? FIXED_DT * 2 : FIXED_DT;

        while ( accumulator >= step_dt && steps < Game.max_physics_steps )
        {
          PhysicsWorld.update( step_dt );
          accumulator -= step_dt;
          steps++;

          if ( performance.now() - physics_start >= Game.physics_budget_ms )
            break;
        }

        if ( Game.physics_adaptive && steps > 0 )
        {
          const per_step = PhysicsWorld.step_ms / steps;

          if ( per_step >= Game.physics_slow_enter_ms )
            Game.physics_slow_mode = true;
          else if ( per_step <= Game.physics_slow_exit_ms )
            Game.physics_slow_mode = false;
        }

        // A long frame (system stall, GC, GPU hitch) leaves the accumulator in
        // debt. Paying it all back in the next frame makes that frame long too,
        // which snowballs — so the debt is dropped instead.
        if ( accumulator >= step_dt )
          accumulator = 0;

        PhysicsDebugRenderer.update();

        FpsOverlay.mark( "phys", performance.now() - frame_start );
        FpsOverlay.markPhysics( PhysicsWorld.step_ms, PhysicsWorld.hook_ms, PhysicsWorld.counts() );

        const t_screen = performance.now();
        ScreenManager.update( dt );
        FpsOverlay.mark( "game", performance.now() - t_screen );
      }
      else
      {
        accumulator = 0; // [SIM] щоб не було "черги" кроків після симуляції
      }

      const now = performance.now();

      if ( this.loading_screen )
      {
        this.loading_screen.setShownProgress( StartupProgress.update( now ) );
        this.loading_screen.update( now );
        this.loading_screen.render();
      }

      if ( this.loading_game_screen )
      {
        this.loading_game_screen.setProgress( 0 );
        this.loading_game_screen.update( now );
        this.loading_game_screen.render();
      }

      const t_ui = performance.now();
      this.animationsUpdateAll( now );
      this.renderAll();
      FpsOverlay.mark( "ui2d", performance.now() - t_ui );

      const t_gl = performance.now();
      if ( ScreenManager.getCurrentScreen() === Screens.GAME && !this.isSceneRevealHeld( now ) )
      {
        Renderer.render( scene, SceneManager.camera );
        this.scene_revealed = true;
      }
      FpsOverlay.mark( "gl", performance.now() - t_gl );

      FpsOverlay.markSteps( steps );
      FpsOverlay.tick( performance.now() );
      UiPerfProbe.onFrame( performance.now() );
      PerfSession.onFrameEnd( performance.now() );
    };

    loop();

    // A first level needs the 3D models before it can be built; a returning
    // player's menu does not, so only then do they compete with the startup art.
    if ( first_screen_is_game )
      void ResourceManager.startLoading();

    await this.loadStartupResourcesWithRetry();
    // The rest of the art loads while the loader still waits for the network.
    StartupImagePlan.start();
    Vfx2DGlobalAtlas.init( VFX_2D_ATLAS_CONFIG, VFX_2D_ATLAS_SIZE );
    WebGLUIRuntime.setup();
    await Vfx2DRuntime.setup();

    Renderer.renderer.compile( SceneManager.scene, SceneManager.camera );

    this.updateNavigationCanvas();

    OverlayManager.init( this.uiContext( DOM_UI_KEYS.OVERLAY, this.overlay_ctx ), Game.getScreenWidth(), Game.getScreenHeight() );
    CoinFlightHud.init( this.game_x, Game.getScreenWidth(), Game.getScreenHeight(), Game.dots_per_pixel );

    const navigation_height = LayoutScaler.heightFromDesign( 400 ) + Game.SAFE_AREA_BOTTOM;
    const navigation_y = LayoutScaler.yFromDesign( 0 );

    this.navigation_block = this.createNavigationBlock(
      this.uiContext( DOM_UI_KEYS.NAVIGATION, this.navigation_ctx ),
      Game.SAFE_AREA_LEFT,
      navigation_y,
      Game.getContentWidth(),
      navigation_height
    )

    const ENTRANCE_BUFFER_MS = 100;
    const MAX_ENTRANCE_WAIT_MS = 3000;

    const entrance_remaining = LoadingScreen.ENTRANCE_DURATION_MS + ENTRANCE_BUFFER_MS - ( performance.now() - loading_screen_started_at );
    const entrance_wait = Math.min( Math.max( entrance_remaining, 0 ), MAX_ENTRANCE_WAIT_MS );
    if ( entrance_wait > 0 )
      await new Promise( r => setTimeout( r, entrance_wait ) );

    void ResourceManager.startLoading();
    void ImagesHolder.preloadDeferred( 2 );
    // Dialog prewarm builds six dialogs and the avatar picker needs every
    // avatar, which are deferred art: run it once that art is in, off the
    // startup path, instead of competing with the first screen.
    DialogManager.setPrewarmAllowed( () =>
      ScreenManager.getCurrentScreen() === Screens.MENU
      && !OverlayManager.isAnyOverlayOpened()
      && !this.isLoadingGameScreenShown() );
    StartupImagePlan.runWhenReady( () =>
    {
      void DialogManager.prewarmDialogs( [
        { key: "PlayDialog", create: () => new PlayDialog( StatisticsStorage.load().level ) },
        { key: "SettingsDialog", create: () => new SettingsDialog() },
        { key: "MoreLivesDialog", create: () => new MoreLivesDialog() },
        { key: "ProfileDialog", create: () => new ProfileDialog() },
        { key: "LeaveLevelDialog", create: () => new LeaveLevelDialog() },
        { key: "AvatarSelectionDialog", create: () => new AvatarSelectionDialog( () => {} ) },
      ] );
      void BackgroundWork.run( "avatar gpu", () => ImagesHolder.warmupGpu( [
        ...Array.from( { length: 20 }, ( _, i ) => AvatarUtils.getAvatarImgId( i ) ),
        ImgID.AvatarCircle,
        ImgID.UI_AvatarSelectActiveBtn,
        ImgID.UI_AvatarSelectInactiveBtn,
        ImgID.IconAvatarSelected,
        ImgID.UI_SettingsDialogCloseBtn,
        ImgID.UI_CapsuleBlue,
      ] ) );
    } );

    // A batch from the editor runs headless and never shows a screen at all.
    if ( TestLevelBridge.isBatchActive() )
    {
      this.hideStartupLoadingScreen();
      if ( TestLevelBridge.isCollector() )
        runSimulationCollector();
      else if ( TestLevelBridge.isSimBatch() )
        void runSimulationBatch();
      else
        void runStabilityBatch();
      return;
    }

    // A level handed over by the editor skips the menu and starts straight away.
    // Big (cube-shaped) levels no longer fit in the URL — the editor sends a
    // "pending" marker instead and posts the real payload once we say we're
    // ready; wait for that handshake before reading the level back out.
    if ( TestLevelBridge.isPendingSingle() )
      await TestLevelBridge.awaitPendingSingle();

    // The startup loader stays up until the first screen is actually built:
    // a level takes it over as its loading screen (see showLoadingGameScreen),
    // the menu waits for its server-backed state first. Hiding it earlier left
    // a bare background while the network data and the level were loading.
    const test_level = TestLevelBridge.getLevel();
    if ( test_level !== null )
      ScreenManager.showScreen( Screens.GAME, { level: test_level } );
    else if ( Game.isFirstTimePlayer() )
      ScreenManager.showScreen( Screens.GAME, { level: 1 } );
    else
    {
      await this.menu_startup_ready;
      // The menu's first frame (DOM layout + sync image decode) takes up to a
      // second on devices. Hiding the loader before that frame is presented
      // left the bare background on screen, so wait for two frames first.
      void ScreenManager.showScreen( Screens.MENU )
        .then( () => this.waitForLoadingGameScreenPaint() )
        .finally( () => this.hideStartupLoadingScreen() );
      ScreenManager.schedulePrewarm();
    }

    audioController.startBackgroundLoading();
    console.log('Loaded bytes:', "images:", ImagesHolder.getDownloadedBytes(), "resources:", ResourceManager.getDownloadedBytes(), "audio:", audioController.getDownloadedBytes());
  }

  private startup_loader_hiding = false;

  // The bar fills to 100% only here, in a quick run-up right before the
  // loader goes away (see StartupProgress); the loader stays for that run-up.
  private hideStartupLoadingScreen(): void
  {
    if ( !this.loading_screen || this.startup_loader_hiding )
      return;
    StartupProgress.finish();
    this.startup_loader_hiding = true;

    const started_at = performance.now();
    const tick = () =>
    {
      if ( !this.loading_screen )
        return;
      if ( StartupProgress.shownValue() < 1 && performance.now() - started_at < Game.STARTUP_LOADER_FINISH_MAX_MS )
      {
        requestAnimationFrame( tick );
        return;
      }
      this.loading_screen.cleanup();
      this.loading_screen = undefined;
      this.startup_loader_hiding = false;
      // A level's cannon loader may have taken the canvas over meanwhile.
      if ( !this.loading_game_screen )
      {
        this.loading_canvas.style.display = 'none';
        this.releaseScreenCanvas( this.loading_canvas );
      }
    };
    tick();
  }

  private static readonly STARTUP_LOADER_FINISH_MAX_MS = 250;

  private async loadStartupResourcesWithRetry(): Promise<void>
  {
    let delay_ms = 2_000;

    for ( let attempt = 1; ; attempt++ )
    {
      try
      {
        const skip_deferred = ( id: ImgID, url: string ) => StartupImagePlan.isDeferred( id, url );
        const critical_ids = ImagesHolder.getImageIds( [ Screens.MENU, Screens.GAME ], skip_deferred );
        const extras = [
          FontLoader.preloadAll(),
          audioController.loadEssential(),
          LocalizationManager.whenRemoteReady(),
        ];
        StartupProgress.trackAssets( [ ...critical_ids, ...VFX_2D_ATLAS_CONFIG ], extras );
        await Promise.all( [
          ImagesHolder.preloadScreens( [ Screens.MENU, Screens.GAME ], 32, true, skip_deferred ),
          ImagesHolder.preloadImages( VFX_2D_ATLAS_CONFIG ),
          ...extras,
        ] );

        const missing_images = ImagesHolder.getMissingImageIds( [ Screens.MENU, Screens.GAME ], skip_deferred );
        if ( missing_images.length > 0 )
          throw new Error( `Failed to load ${missing_images.length} images (first id: ${missing_images[0]})` );

        if ( attempt > 1 )
          console.info( `[LoadRetry] startup resources loaded on attempt ${attempt}` );
        return;
      }
      catch ( error )
      {
        const network_error = !NetworkManager.isOnline() || isNetworkishError( error );
        if ( network_error )
          NetworkManager.handleNetworkError( error );
        else
          NetworkManager.handleLoadingDataError( error );

        if ( network_error && !NetworkManager.isOfflinePlayable() )
          await NetworkManager.waitForOnline();
        await new Promise( resolve => window.setTimeout( resolve, delay_ms ) );
        delay_ms = Math.min( delay_ms * 2, 15_000 );
      }
    }
  }


  private setupLoadingCanvas( include_bottom_safe_area: boolean = false ): void
  {
    const dpr = Game.getDpr();
    const logical_screen_w = Game.getScreenWidth();
    const logical_screen_h = Game.getScreenHeight()
      + ( include_bottom_safe_area ? Game.safeAreaBottom() : 0 );

    // Assigning width/height throws the backing store away and allocates a new
    // one even when the value is unchanged — a full-screen buffer at device
    // pixel ratio, on every level transition. Only pay that when the size has
    // actually moved; otherwise reset and clear the existing surface by hand.
    const target_w = Math.round( dpr * logical_screen_w );
    const target_h = Math.round( dpr * logical_screen_h );
    const ctx = this.screenContext( this.loading_canvas, DOM_UI_KEYS.LOADING );

    if ( this.loading_canvas.width !== target_w || this.loading_canvas.height !== target_h )
    {
      this.loading_canvas.width = target_w;
      this.loading_canvas.height = target_h;
    }
    else
    {
      ctx.setTransform( 1, 0, 0, 1, 0, 0 );
      ctx.clearRect( 0, 0, target_w, target_h );
    }

    this.loading_canvas.style.left = `${this.game_x}px`;
    this.loading_canvas.style.top = '0px';
    this.loading_canvas.style.width = `${logical_screen_w}px`;
    this.loading_canvas.style.height = `${logical_screen_h}px`;
    this.loading_canvas.style.display = 'block';

    this.loading_ctx = ctx;

    if ( dpr !== 1 )
      this.loading_ctx.scale( dpr, dpr );
  }

  public showLoadingGameScreen( preserve_vfx: boolean = false, variant: LoadingGameVariant = 'default' ): void
  {
    this.loading_game_screen_shown_at = performance.now();
    ScreenManager.hideAll( preserve_vfx );
    this.hideNavigationBlock();

    // The first level after launch keeps the startup loader instead of
    // swapping in a second loading screen; it is hidden with the level's one.
    if ( this.loading_screen && !this.startup_loader_hiding )
    {
      this.startup_loader_as_game_loading = true;
      return;
    }
    this.setupLoadingCanvas();

    const loading_ctx = this.uiContext( DOM_UI_KEYS.LOADING, this.loading_ctx );
    this.loading_game_variant = variant;
    this.loading_game_screen = new LoadingGameScreen(
      loading_ctx,
      0,
      0,
      Game.getScreenWidth(),
      Game.getScreenHeight(),
      variant
    );
    this.loading_game_screen.mountDomHost( this.loading_canvas );

    this.loading_game_screen.update( performance.now() );
    this.loading_game_screen.render();
  }

  public hideLoadingGameScreen(): void
  {
    if ( this.startup_loader_as_game_loading )
    {
      this.startup_loader_as_game_loading = false;
      this.hideStartupLoadingScreen();
    }
    this.loading_game_screen?.cleanup();
    this.loading_game_screen = undefined;
    this.loading_canvas.style.display = "none";
    this.releaseScreenCanvas( this.loading_canvas );
  }

  public isLoadingGameScreenShown(): boolean
  {
    return this.loading_game_screen !== undefined || this.startup_loader_as_game_loading;
  }

  public holdSceneRevealUntil( time_ms: number ): void
  {
    const now = performance.now();
    this.scene_reveal_hold_until = Math.min(
      Math.max( now, time_ms ),
      now + Game.MAX_SCENE_REVEAL_HOLD_MS
    );
    this.scene_revealed = false;
  }

  /** Until when the scene stays undrawn under the loading screen (0 or past: not held). */
  public sceneRevealHoldUntil(): number
  {
    return this.scene_reveal_hold_until;
  }

  private isSceneRevealHeld( now: number ): boolean
  {
    return !this.scene_revealed
      && this.loading_game_screen !== undefined
      && now < this.scene_reveal_hold_until;
  }

  public waitForSceneRevealed( timeout_ms: number = 3_000 ): Promise<void>
  {
    if ( this.scene_revealed )
      return Promise.resolve();

    return new Promise( resolve =>
    {
      let settled = false;
      const finish = () =>
      {
        if ( settled ) return;
        settled = true;
        window.clearTimeout( timeout_id );
        resolve();
      };
      const check = () =>
      {
        if ( settled ) return;
        if ( this.scene_revealed )
        {
          finish();
          return;
        }
        requestAnimationFrame( check );
      };
      const timeout_id = window.setTimeout( finish, timeout_ms );
      requestAnimationFrame( check );
    } );
  }

  public waitForLoadingGameScreenPaint(): Promise<void>
  {
    return new Promise( resolve => requestAnimationFrame( () =>
      requestAnimationFrame( () => resolve() )
    ) );
  }

  public waitForNextGameUIPaint( timeout_ms: number = 1_000 ): Promise<void>
  {
    const revision = Game.gameUIPaintRevision();

    return new Promise( resolve =>
    {
      let settled = false;
      const finish = () =>
      {
        if ( settled ) return;
        settled = true;
        window.clearTimeout( timeout_id );
        resolve();
      };
      const check = () =>
      {
        if ( settled ) return;
        if ( Game.gameUIPaintRevision() > revision )
        {
          finish();
          return;
        }
        requestAnimationFrame( check );
      };
      const timeout_id = window.setTimeout( finish, timeout_ms );
      requestAnimationFrame( check );
    } );
  }

  public async waitForMinimumLoadingGameScreenDuration(): Promise<void>
  {
    const min_duration_ms = 300;
    const elapsed = performance.now() - this.loading_game_screen_shown_at;
    const remaining = Math.max( 0, min_duration_ms - elapsed );
    if ( remaining > 0 )
      await new Promise<void>( resolve => setTimeout( resolve, remaining ) );
  }

  static getYOverflow() { return this.Y_OVERFLOW; }

  public initBackButton(): void
  {
    BackButtonHandler.getInstance().init();
  }

  public setFpsOverlayEnabled( enabled: boolean ): void
  {
    FpsOverlay.setEnabled( enabled );
  }

  public startRenderLoop()
  {
    let prev_t = performance.now();
    const MIN_DT = 1000 / GlobalSettings.RENDERING_MAX_FPS;

    const loop = () =>
    {
      try
      {
        const now = performance.now();
        const dt = now - prev_t;


        if ( dt >= MIN_DT )
        {
          prev_t = now;

          // Оновлення логіки та рендер
          this.animationsUpdateAll( now );
          this.renderAll();
        }


        requestAnimationFrame( loop );
      }
      catch ( e )
      {
        console.error( e );
        crashCapture( e, { phase: 'render_loop' }, false );
      }
    };

    requestAnimationFrame( loop );
  }
}
